import { useCallback, useMemo, useState } from "react";

import { AiOutlineClose } from "react-icons/ai";

import type { ParkingInput, ParkingRecord } from "@/apis/parking";
import Button from "@/components/Button";
import ModalLayout from "@/components/Layout/ModalLayout";

interface Props {
  record: ParkingInput;
  isNew: boolean;
  allData?: ParkingRecord[];
  editId?: number | null;
  onSave: (record: ParkingInput) => void;
  onClose: () => void;
  onViewExisting?: (item: ParkingRecord) => void;
}

const requiredKeys: (keyof ParkingInput)[] = ["name", "phone", "plate"];

const fields: { key: keyof ParkingInput; label: string; placeholder: string }[] = [
  { key: "name", label: "이름", placeholder: "예: 홍길동 / 김철수" },
  { key: "phone", label: "연락처", placeholder: "예: 010-1234-5678 / 010-9876-5432" },
  { key: "community", label: "공동체", placeholder: "예: 베드로1순" },
  { key: "team", label: "사역팀", placeholder: "예: 찬양사역팀" },
  { key: "car", label: "차종", placeholder: "예: 쏘렌토 / 아반떼" },
  { key: "plate", label: "차량번호", placeholder: "예: 12가 3456 / 34나 5678" },
];

const normalize = (s: string) => s.replace(/[\s\-/]/g, "").toLowerCase();

/** 전화번호 하나를 010-1234-5678 형식으로 포맷. 끝이 -이면 그대로 유지 */
const formatPhone = (raw: string) => {
  const endsWithHyphen = raw.endsWith("-");
  const digits = raw.replace(/\D/g, "");
  let result: string;
  if (digits.length <= 3) result = digits;
  else if (digits.length <= 7) result = `${digits.slice(0, 3)}-${digits.slice(3)}`;
  else result = `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
  if (endsWithHyphen && !result.endsWith("-")) result += "-";
  return result;
};

/** " / " 구분자를 유지하면서 각 번호를 포맷 */
const formatPhoneValue = (value: string) =>
  value.split("/").map((part) => {
    const trimmed = part.trim();
    if (!trimmed) return part;
    return part.startsWith(" ") ? ` ${formatPhone(trimmed)}` : formatPhone(trimmed);
  }).join("/");

const ParkingEditModal = ({
  record,
  isNew,
  allData,
  editId,
  onSave,
  onClose,
  onViewExisting,
}: Props) => {
  const [form, setForm] = useState<ParkingInput>({ ...record });
  const [dismissedDuplicates, setDismissedDuplicates] = useState(false);
  const [touched, setTouched] = useState<Set<keyof ParkingInput>>(new Set());

  const handleChange = (key: keyof ParkingInput, value: string) => {
    const formatted = key === "phone" ? formatPhoneValue(value) : value;
    setForm((prev) => ({ ...prev, [key]: formatted }));
    setTouched((prev) => new Set(prev).add(key));
    if (key === "name" || key === "phone") {
      setDismissedDuplicates(false);
    }
  };

  const duplicates = useMemo(() => {
    if (!isNew || !allData || dismissedDuplicates) return [];

    const nameNorm = normalize(form.name);
    const phoneNorm = normalize(form.phone);
    if (!nameNorm && !phoneNorm) return [];

    const matches: ParkingRecord[] = [];

    allData.forEach((item) => {
      if (editId != null && item.id === editId) return;

      const itemName = normalize(item.name);
      const itemPhone = normalize(item.phone);

      const nameMatch = nameNorm && itemName && itemName.includes(nameNorm);
      const phoneMatch = phoneNorm && itemPhone && itemPhone.includes(phoneNorm);

      if (nameMatch || phoneMatch) {
        matches.push(item);
      }
    });

    return matches;
  }, [form.name, form.phone, allData, isNew, editId, dismissedDuplicates]);

  const handleSubmit = () => {
    const missing = requiredKeys.filter((k) => !form[k].trim());
    if (missing.length > 0) {
      setTouched((prev) => {
        const next = new Set(prev);
        missing.forEach((k) => next.add(k));
        return next;
      });
      return;
    }
    onSave(form);
  };

  const handleViewExisting = useCallback(
    (item: ParkingRecord) => {
      onViewExisting?.(item);
    },
    [onViewExisting],
  );

  const isFieldError = (key: keyof ParkingInput) =>
    requiredKeys.includes(key) && touched.has(key) && !form[key].trim();

  return (
    <ModalLayout variant="primary" onClose={onClose}>
      <div className="relative z-50 flex h-[580px] w-[420px] flex-col px-6 py-5">
        {/* 헤더 */}
        <div className="mb-4 flex shrink-0 items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">
            {isNew ? "주차정보 추가" : "주차정보 수정"}
          </h3>
          <Button variant="icon" onClick={onClose}>
            <AiOutlineClose size={18} />
          </Button>
        </div>

        {/* 스크롤 영역 */}
        <div className="flex-1 overflow-y-auto">
          {/* 중복 경고 */}
          {duplicates.length > 0 && (
            <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="mb-2 text-xs font-semibold text-amber-700">
                이름 또는 연락처가 일치하는 기존 데이터가 {duplicates.length}건
                있습니다.
              </p>
              <div className="flex max-h-28 flex-col gap-1.5 overflow-y-auto">
                {duplicates.map((dup) => (
                  <div
                    key={dup.id}
                    className="flex items-center justify-between rounded-md bg-white px-2.5 py-1.5 text-xs"
                  >
                    <span className="text-gray-700">
                      {dup.name}
                      {dup.plate && (
                        <span className="ml-1.5 text-gray-400">({dup.plate})</span>
                      )}
                    </span>
                    {onViewExisting && (
                      <button
                        type="button"
                        onClick={() => handleViewExisting(dup)}
                        className="shrink-0 text-primary underline hover:text-primary/70"
                      >
                        상세보기
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setDismissedDuplicates(true)}
                className="mt-2 text-xs text-amber-600 underline hover:text-amber-800"
              >
                무시하고 계속 작성
              </button>
            </div>
          )}

          <div className="flex flex-col gap-3">
            {fields.map(({ key, label, placeholder }) => {
              const required = requiredKeys.includes(key);
              const error = isFieldError(key);
              return (
                <div key={key}>
                  <label className="mb-1 block text-xs font-medium text-gray-500">
                    {label}
                    {required && <span className="ml-0.5 text-red-400">*</span>}
                  </label>
                  <input
                    type="text"
                    value={form[key]}
                    placeholder={placeholder}
                    onChange={(e) => handleChange(key, e.target.value)}
                    className={`h-10 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1 ${
                      error
                        ? "border-red-300 focus:border-red-400 focus:ring-red-200"
                        : "border-gray-200 focus:border-primary focus:ring-primary/20"
                    }`}
                  />
                  {error && (
                    <p className="mt-0.5 text-xs text-red-400">
                      {label}
                      {(() => {
                        const last = label.charCodeAt(label.length - 1);
                        return (last - 0xac00) % 28 > 0 ? "을" : "를";
                      })()}{" "}
                      입력해주세요.
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <p className="mt-2 text-xs text-gray-400">
            여러 값은 / 로 구분해주세요.
          </p>
        </div>

        {/* 하단 버튼 */}
        <div className="mt-4 flex shrink-0 justify-end gap-2">
          <Button
            variant="custom"
            onClick={onClose}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
          >
            취소
          </Button>
          <Button
            variant="custom"
            onClick={handleSubmit}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            {isNew ? "추가" : "저장"}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
};

export default ParkingEditModal;
